import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import {
  Avatar,
  Button,
  ButtonBase,
  Link,
  MenuItem,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import {
  LinearUser,
  LinearUserOpen,
  MobileUser,
  SignOut,
  UpdatePersonalDetails,
  ViewDisabledVeteranCard,
  ViewPersonalDetails,
  WhiteArrowRight,
} from 'Images';
import { useState } from 'react';
import { RoutesConfig } from 'Routes';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

export const UserAccordion = () => {
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.Header);
  const [navMenuButtonAnchorEl, setNavMenuButtonAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const { instance, inProgress } = useMsal();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setNavMenuButtonAnchorEl((prevAnchor) => {
      if (!prevAnchor) {
        return event.currentTarget;
      } else {
        return null;
      }
    });
  };

  const handleClose = () => {
    setNavMenuButtonAnchorEl(null);
  };

  const open = Boolean(navMenuButtonAnchorEl);
  const navMenuId = open ? 'nav-menu-popover' : undefined;

  const menuItems = dictionary.personalButton.menuItems;

  const menuItemsWithIcon = [
    {
      label: menuItems.viewPersonalDetails.label,
      href: process.env.PUBLIC_URL + RoutesConfig.PERSONAL_DETAILS.PATH,
      icon: ViewPersonalDetails,
    },
    {
      label: menuItems.updatePersonalDetails.label,
      href: process.env.PUBLIC_URL + '/requests/update-personal-details/8/18',
      icon: UpdatePersonalDetails,
    },
    {
      label: menuItems.viewDisabledVeteranCertificate.label,
      href: RoutesConfig.DISABLED_VETERAN.PATH,
      icon: ViewDisabledVeteranCard,
    },
    {
      label: menuItems.signout.label,
      onClick: async () => {
        if (inProgress === InteractionStatus.None) {
          sessionStorage.removeItem('B2CToken');
          sessionStorage.removeItem('token');
          await instance.logoutRedirect();
        }
      },
      icon: SignOut,
    },
  ];

  return (
    <>
      {isDesktop ? (
        <Button
          variant='primary1'
          aria-controls={navMenuId}
          aria-haspopup='menu'
          onClick={handleClick}
          sx={{
            width: '198px',
            height: '48px',
            borderRadius: '8px',
            zIndex: '2002',
          }}
        >
          <Stack
            direction={'row'}
            justifyContent={'center'}
            alignItems='center'
            gap={'8px'}
          >
            <Avatar
              alt={''}
              src={LinearUser}
              sx={{ width: '28px', height: '28px' }}
            />
            <Typography variant={'body1'} color={'#fff'} whiteSpace='nowrap'>
              {dictionary.personalButton.title}
            </Typography>
            <Avatar
              alt={''}
              src={WhiteArrowRight}
              sx={{
                ml: '-10px',
                width: '24px',
                height: '24px',
                rotate: open ? '90deg' : '180deg',
              }}
            />
          </Stack>
        </Button>
      ) : (
        <ButtonBase aria-describedby={navMenuId} onClick={handleClick}>
          <Avatar
            alt={''}
            src={open ? LinearUserOpen : MobileUser}
            sx={{ width: '30px', height: '30px' }}
          />
        </ButtonBase>
      )}
      <Popover
        aria-live='polite'
        disablePortal={true}
        id={navMenuId}
        open={open}
        anchorEl={navMenuButtonAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          zIndex: 2001,
          right: isDesktop ? undefined : '-2px',
          top: isDesktop ? '-15px' : '-1px',
        }}
        PaperProps={{
          sx: {
            borderRadius: '10px 0 10px 10px',
            boxShadow: '0',
            bgcolor: 'transparent',
          },
        }}
      >
        <Stack
          component={'ul'}
          sx={{ padding: '10px 0', margin: 0, width: '198px' }}
          bgcolor={'primary.main'}
        >
          {menuItemsWithIcon.map((item, index) => (
            <Link
              key={index}
              underline={'none'}
              color={'#fff'}
              href={item.href}
              onClick={item.onClick}
              tabIndex={0}
            >
              <MenuItem
                sx={{
                  padding: '8px 16px',
                  gap: '4px',
                  '&:hover': { backgroundColor: 'primary.shade_600' },
                }}
              >
                <Avatar
                  alt={''}
                  src={item.icon}
                  sx={{ width: '24px', height: '24px' }}
                />
                <Typography variant={'body3'} color={'#fff'}>
                  {item.label}
                </Typography>
              </MenuItem>
            </Link>
          ))}
        </Stack>
      </Popover>
    </>
  );
};
